import { useState, useEffect, useCallback, useRef } from "react";
import { POLLING_INTERVAL, EVENT_STATUS } from "../../constants/newyearConstants";

const ACTIVE_STATUSES = [EVENT_STATUS.OPEN, EVENT_STATUS.PAUSED];

const useEventPolling = (fetchFn, options = {}) => {
  const {
    interval = POLLING_INTERVAL.STATUS,
    slowInterval = POLLING_INTERVAL.STATUS_SLOW,
    enabled = true,
    onError,
    onSuccess,
    shouldPoll,
    pauseOnHidden = true,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const mountedRef = useRef(true);
  const fetchFnRef = useRef(fetchFn);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const shouldPollRef = useRef(shouldPoll);
  const dataRef = useRef(data);

  // Keep refs updated
  useEffect(() => {
    fetchFnRef.current = fetchFn;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    shouldPollRef.current = shouldPoll;
    dataRef.current = data;
  });

  const getPollingInterval = useCallback(
    (currentData) => {
      if (currentData) {
        const status = currentData.status || currentData.eventStatus;
        if (status && !ACTIVE_STATUSES.includes(status)) {
          return slowInterval;
        }
      }
      return interval;
    },
    [interval, slowInterval]
  );

  const canPoll = useCallback(() => {
    if (!enabled) return false;
    if (pauseOnHidden && document.hidden) return false;
    if (shouldPollRef.current && typeof shouldPollRef.current === "function") {
      return shouldPollRef.current(dataRef.current);
    }
    return true;
  }, [enabled, pauseOnHidden]);

  const clearPollingInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const result = await fetchFnRef.current();
      if (mountedRef.current) {
        setData(result);
        dataRef.current = result;
        setError(null);
        onSuccessRef.current?.(result);
      }
      return result;
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        onErrorRef.current?.(err);
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const startPolling = useCallback(
    (currentData) => {
      clearPollingInterval();

      if (!enabled) return;

      const pollingInterval = getPollingInterval(currentData);

      const poll = async () => {
        if (!canPoll()) return;

        const result = await fetchData();

        // Check if interval needs to change based on new data
        if (result && mountedRef.current) {
          const newInterval = getPollingInterval(result);
          if (newInterval !== pollingInterval) {
            // Restart with new interval
            startPolling(result);
          }
        }
      };

      intervalRef.current = setInterval(poll, pollingInterval);
    },
    [enabled, canPoll, fetchData, getPollingInterval, clearPollingInterval]
  );

  const stopPolling = useCallback(() => {
    clearPollingInterval();
  }, [clearPollingInterval]);

  const refetch = useCallback(async () => {
    setLoading(true);
    const result = await fetchData();
    return result;
  }, [fetchData]);

  // Initial fetch and polling setup
  useEffect(() => {
    mountedRef.current = true;

    if (!enabled) {
      return () => {
        mountedRef.current = false;
      };
    }

    const init = async () => {
      const result = await fetchData();
      if (mountedRef.current && enabled) {
        startPolling(result);
      }
    };

    init();

    return () => {
      mountedRef.current = false;
      clearPollingInterval();
    };
  }, [enabled]); // Only depend on enabled

  // Handle visibility change
  useEffect(() => {
    if (!pauseOnHidden) return;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        clearPollingInterval();
      } else if (enabled) {
        // Page became visible - fetch immediately and restart polling
        const result = await fetchData();
        if (mountedRef.current) {
          startPolling(result);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pauseOnHidden, enabled, fetchData, startPolling, clearPollingInterval]);

  return {
    data,
    loading,
    error,
    refetch,
    startPolling: () => startPolling(dataRef.current),
    stopPolling,
  };
};

export default useEventPolling;
